import { useTranslation } from 'react-i18next';

type RawPaginationItem = number | '...';

export type PaginationEllipsisItem = {
  key: string;
  type: 'ellipsis';
};

export type PaginationPageItem = {
  ariaLabel: string;
  isActive: boolean;
  key: string;
  onClick: () => void;
  page: number;
  type: 'page';
};

export type PaginationListItem = PaginationEllipsisItem | PaginationPageItem;

type UsePaginationParams = {
  ariaLabel?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
};

export type UsePaginationReturn = {
  ariaNextLabel: string;
  ariaPrevLabel: string;
  canNextPage: boolean;
  canPreviousPage: boolean;
  items: PaginationListItem[];
  onNextPage: () => void;
  onPreviousPage: () => void;
  resolvedAriaLabel: string;
};

function getPaginationRange(current: number, total: number): RawPaginationItem[] {
  const delta = 2;
  const range: number[] = [];
  const rangeWithDots: RawPaginationItem[] = [];
  let l: number | undefined;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l !== undefined) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
}

function usePagination({ ariaLabel, currentPage, totalPages, onPageChange }: UsePaginationParams): UsePaginationReturn {
  const { t } = useTranslation();

  const canPreviousPage = currentPage > 1;
  const canNextPage = currentPage < totalPages;
  const resolvedAriaLabel = ariaLabel ?? t('common:PaginationNavLabel');
  const ariaPrevLabel = t('common:PaginationPrevLabel');
  const ariaNextLabel = t('common:PaginationNextLabel');

  function getPageAriaLabel(page: number): string {
    return page === currentPage
      ? t('common:PaginationCurrentPageLabel', { page })
      : t('common:PaginationPageLabel', { page });
  }

  const rawItems = getPaginationRange(currentPage, totalPages);

  const items: PaginationListItem[] = rawItems.map((item, idx) => {
    if (item === '...') {
      const prevPage = idx > 0 ? rawItems[idx - 1] : 0;
      return { key: `ellipsis-after-${prevPage}`, type: 'ellipsis' };
    }

    const page = item;

    function onClick(): void {
      onPageChange(page);
    }

    return {
      ariaLabel: getPageAriaLabel(page),
      isActive: page === currentPage,
      key: `page-${page}`,
      onClick,
      page,
      type: 'page',
    };
  });

  function onPreviousPage(): void {
    if (canPreviousPage) onPageChange(currentPage - 1);
  }

  function onNextPage(): void {
    if (canNextPage) onPageChange(currentPage + 1);
  }

  return {
    ariaNextLabel,
    ariaPrevLabel,
    canNextPage,
    canPreviousPage,
    items,
    onNextPage,
    onPreviousPage,
    resolvedAriaLabel,
  };
}

export default usePagination;
